import {
  BeamConstancyWorkItemDto,
  BeamConstancyWorkStepDto,
  instanceOfUnicheckDeviceDto,
  instanceOfUnicheckSimulatorDto,
  JobOccurrenceDto,
  MedicalSystemCatalogDto,
  MedicalSystemRtSystemDto,
  QaEquipmentCatalogDto,
  QaEquipmentRtSystemDto,
  RunBeamConstancyMeasurementRequestDto,
} from '@/api/generator';
import { Measurement } from '../types';
import { computed, MaybeRefOrGetter, toRef, toValue } from 'vue';
import { api } from '@/api';
import { computedAsync } from '@vueuse/core';
import { truthy } from '@/utils/helper';
import { useDeviceStatusSignalR } from '@/components/deviceStatus/utils/useDeviceStatusSignalR';
import { getReferenceMeasurementNotification } from './measurementNotifications';
import { useJobOccurrence } from '@/views/jobOccurrences/utils/useJobOccurrence';
import { useSupportSystemGroups } from '@/views/jobConfigurations/utils/useSupportSystemGroups';

function createMeasurement(
  jobOccurrence: Partial<JobOccurrenceDto>,
  task: BeamConstancyWorkItemDto,
  workStep: BeamConstancyWorkStepDto,
  targetSystem?: MedicalSystemRtSystemDto | MedicalSystemCatalogDto,
  supportSystems: QaEquipmentRtSystemDto[] | QaEquipmentCatalogDto[] = [],
): Measurement | undefined {
  const skip = workStep.workState === 'Skipped';

  const notifications = [
    getReferenceMeasurementNotification(
      workStep.combination?.referenceMeasurementInfo?.referenceMeasurementState,
    ),
  ].filter(truthy);

  return targetSystem
    ? ({
        targetSystem,
        supportSystems,
        request: {
          workItemId: task.id,
          parameterCombinationHash: workStep.combination?.hash,
          jobOccurrenceId: jobOccurrence.id,
          $type:
            'Ptw.Ptw100.AppZero.Application.Shared.JobExecution.Dto.RunBeamConstancyMeasurementRequestDto, Ptw.Ptw100.AppZero.Application.Shared',
        } satisfies RunBeamConstancyMeasurementRequestDto,
        setup: {
          targetSystem: workStep.combination?.targetSystemValues ?? undefined,
          supportSystem: workStep.combination?.supportSystemValues ?? undefined,
        },
        disabled: !workStep.isExecutable || skip,
        skip,
        notifications, //TODO @Marc: Add notifications if disabled
        evaluations: workStep.evaluations ?? undefined,
      } satisfies Measurement)
    : undefined;
}

async function getMeasurementResult(measurementId: string) {
  const { result } = await api.measurementDataApi.getMeasurementAnalysisResults({
    measurementGroupId: measurementId,
  });

  return result;
}

export function useBeamConstancyCheckTask(task: MaybeRefOrGetter<BeamConstancyWorkItemDto>) {
  const { jobOccurrence: jobOccurrenceRef, supportSystems: allSupportSystems } = useJobOccurrence();

  const taskRef = toRef(task);

  const targetSystem = computed(() => jobOccurrenceRef.value.targetSystem ?? undefined);

  const { supportSystems, supportSystemGroups, numGroups } = useSupportSystemGroups(
    computed(() => taskRef.value.supportSystemIds ?? undefined),
    computed(() => allSupportSystems.value ?? undefined),
  );

  const device = computed<QaEquipmentRtSystemDto | QaEquipmentCatalogDto | undefined>(() => {
    return supportSystems.value?.find(
      (system) => instanceOfUnicheckDeviceDto(system) || instanceOfUnicheckSimulatorDto(system),
    );
  });

  const deviceStatus = useDeviceStatusSignalR(computed(() => device.value?.id));

  const measurements = computed(() => {
    return Object.fromEntries<Measurement | undefined>(
      taskRef.value.workSteps?.map((workStep) => [
        workStep.combination?.hash ?? '',
        createMeasurement(
          jobOccurrenceRef.value,
          taskRef.value,
          workStep,
          targetSystem.value,
          supportSystems.value,
        ),
      ]) || [],
    );
  });

  function getMeasurement(workStep: BeamConstancyWorkStepDto): Measurement | undefined {
    const key = workStep.combination?.hash;

    if (key) {
      return measurements.value[key];
    }
  }

  const measurementSequence = computed(() =>
    Object.values<Measurement | undefined>(measurements.value || {}).filter(truthy),
  );

  const results = computedAsync(
    async () =>
      await Promise.all(
        toValue(task).workSteps?.map(async (workstep) => ({
          [workstep.combination?.hash?.toString() ?? '']: workstep.combination
            ?.referenceMeasurementInfo?.measurementId
            ? await getMeasurementResult(
                workstep.combination?.referenceMeasurementInfo?.measurementId,
              )
            : undefined,
        })) || [],
      ),
  );

  function getResult(measurementId: string | undefined) {
    return measurementId
      ? toValue(task)
          .workSteps?.flatMap((step) => step.results ?? [])
          .find((result) => result?.measurementId === measurementId)
      : undefined;
  }

  return {
    targetSystem,
    supportSystems,
    supportSystemGroups,
    numGroups,
    device,
    deviceStatus,
    measurementSequence,
    getMeasurement,
    results,
    getResult,
  };
}
